import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../ui/alert-dialog';
import { Plus, PencilSimple, Trash, ArrowUp, ArrowDown, Link as LinkIcon, Info } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LANGS = ['en', 'fr', 'mg'];
const emptyI18n = () => ({ en: '', fr: '', mg: '' });

const I18nFields = ({ value, onChange, label }) => (
  <div className="space-y-2">
    <Label className="text-xs font-semibold">{label}</Label>
    {LANGS.map(l => (
      <Input
        key={l}
        placeholder={l.toUpperCase()}
        value={value[l] || ''}
        onChange={(e) => onChange({ ...value, [l]: e.target.value })}
        className="text-sm"
        data-testid={`nav-i18n-input-${l}`}
      />
    ))}
  </div>
);

export const HeaderNavManagement = () => {
  const [links, setLinks] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, mode: 'create', data: null });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [l, p] = await Promise.all([
        axios.get(`${API}/admin/nav`, { withCredentials: true }),
        axios.get(`${API}/admin/footer/pages-choices`, { withCredentials: true }),
      ]);
      setLinks(l.data || []);
      setPages(p.data || []);
    } catch {
      toast.error('Failed to load navigation data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => setDialog({
    open: true, mode: 'create',
    data: { title_i18n: emptyI18n(), url: '', external: false, is_active: true, page_slug: null, link_type: 'page' },
  });

  const openEdit = (link) => setDialog({
    open: true, mode: 'edit',
    data: {
      ...link,
      title_i18n: { ...emptyI18n(), ...link.title_i18n },
      is_active: link.is_active !== false,
      link_type: (link.external || (link.url || '').startsWith('http')) ? 'external' : 'page',
    },
  });

  const save = async () => {
    const { mode, data } = dialog;
    if (!LANGS.some(l => data.title_i18n[l]?.trim())) {
      toast.error('Title required in at least one language'); return;
    }
    let url = (data.url || '').trim();
    let external = false;
    let page_slug = null;
    if (data.link_type === 'page') {
      if (!data.page_slug) { toast.error('Please pick a page from the dropdown'); return; }
      page_slug = data.page_slug;
      url = `/pages/${data.page_slug}`;
    } else {
      if (!url) { toast.error('URL required'); return; }
      if (!(url.startsWith('/') || url.startsWith('http'))) {
        toast.error("URL must start with '/' or 'http(s)://'"); return;
      }
      external = url.startsWith('http');
    }
    const payload = { title_i18n: data.title_i18n, url, external, is_active: !!data.is_active, page_slug };
    try {
      if (mode === 'create') {
        await axios.post(`${API}/admin/nav`, payload, { withCredentials: true });
      } else {
        await axios.put(`${API}/admin/nav/${data.link_id}`, payload, { withCredentials: true });
      }
      setDialog({ open: false, mode: 'create', data: null });
      fetchAll();
      toast.success(mode === 'create' ? 'Link added' : 'Link updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const toggleActive = async (link) => {
    const nextActive = !(link.is_active !== false);
    try {
      await axios.put(`${API}/admin/nav/${link.link_id}`, { is_active: nextActive }, { withCredentials: true });
      fetchAll();
      toast.success(nextActive ? 'Link enabled' : 'Link disabled');
    } catch { toast.error('Failed'); }
  };

  const remove = async (link) => {
    try {
      await axios.delete(`${API}/admin/nav/${link.link_id}`, { withCredentials: true });
      fetchAll();
      toast.success('Link deleted');
    } catch { toast.error('Failed'); }
  };

  const move = async (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= links.length) return;
    const reordered = [...links];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    try {
      await axios.put(`${API}/admin/nav/reorder`, { link_ids: reordered.map(l => l.link_id) }, { withCredentials: true });
      fetchAll();
    } catch { toast.error('Reorder failed'); }
  };

  if (loading) return <div className="text-center py-12"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="header-nav-management">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-text_main">Header Navigation</h3>
          <p className="text-sm text-text_muted mt-1">Custom top-menu links. Rendered <strong>in addition to</strong> the built-in Browse / Publish / Language / Profile actions — those are never removed.</p>
        </div>
        <Button onClick={openCreate} data-testid="nav-add-btn">
          <Plus size={16} weight="bold" className="mr-1" /> Add Link
        </Button>
      </div>

      <div className="bg-info-soft border border-info/40 rounded-lg px-3 py-2 text-xs text-text_main flex items-start gap-2">
        <Info size={14} className="text-info shrink-0 mt-0.5" weight="fill" />
        <span>System links (Browse, Publish an ad, Notifications, Profile, Language) are always visible. Layout links appear next to them in the desktop header.</span>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-hidden divide-y divide-border">
        {links.length === 0 && (
          <p className="text-sm text-text_muted text-center py-8">No custom nav links yet.</p>
        )}
        {links.map((link, idx) => {
          const active = link.is_active !== false;
          return (
            <div key={link.link_id} className={`flex items-center gap-3 px-4 py-3 ${active ? '' : 'bg-surface_muted/60'}`} data-testid={`admin-nav-link-${link.link_id}`}>
              <div className="flex flex-col gap-0.5">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="disabled:opacity-30" aria-label="Move up"><ArrowUp size={12} weight="bold" /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === links.length - 1} className="disabled:opacity-30" aria-label="Move down"><ArrowDown size={12} weight="bold" /></button>
              </div>
              <LinkIcon size={14} weight="regular" className="text-text_muted flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${active ? 'text-text_main' : 'text-text_muted line-through'}`}>
                  {link.title_i18n?.en || link.title_i18n?.fr || '(untitled)'}
                </p>
                <p className="text-xs text-text_muted truncate">
                  <code>{link.url}</code>
                  {link.page_slug && <span className="ml-2 bg-primary-soft text-primary px-1.5 rounded">page: {link.page_slug}</span>}
                  {link.external && <span className="ml-2 bg-warning-soft text-warning px-1.5 rounded">external</span>}
                  {!active && <span className="ml-2 bg-surface_muted text-text_muted px-1.5 rounded border border-border">inactive</span>}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Switch checked={active} onCheckedChange={() => toggleActive(link)} data-testid={`nav-toggle-${link.link_id}`} />
                <Button size="sm" variant="ghost" onClick={() => openEdit(link)}><PencilSimple size={14} weight="regular" /></Button>
                <Button size="sm" variant="ghost" className="text-danger" onClick={() => setConfirmDelete(link)}><Trash size={14} weight="regular" /></Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={dialog.open} onOpenChange={(v) => !v && setDialog({ open: false, mode: 'create', data: null })}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{dialog.mode === 'create' ? 'New Nav Link' : 'Edit Nav Link'}</DialogTitle></DialogHeader>
          {dialog.data && (
            <div className="space-y-4">
              <I18nFields label="Title (each language)" value={dialog.data.title_i18n} onChange={(v) => setDialog({ ...dialog, data: { ...dialog.data, title_i18n: v } })} />
              <div>
                <Label className="text-xs font-semibold">Link destination</Label>
                <Select value={dialog.data.link_type} onValueChange={(v) => setDialog({ ...dialog, data: { ...dialog.data, link_type: v, page_slug: v === 'page' ? dialog.data.page_slug : null, url: v === 'external' ? dialog.data.url : '' } })}>
                  <SelectTrigger data-testid="nav-behavior-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="page">Point to a Page (recommended)</SelectItem>
                    <SelectItem value="external">External URL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {dialog.data.link_type === 'page' ? (
                <div>
                  <Label className="text-xs font-semibold">Page</Label>
                  <Select value={dialog.data.page_slug || ''} onValueChange={(v) => setDialog({ ...dialog, data: { ...dialog.data, page_slug: v } })}>
                    <SelectTrigger data-testid="nav-page-select"><SelectValue placeholder="Select a page…" /></SelectTrigger>
                    <SelectContent>
                      {pages.length === 0 && <SelectItem value="__none" disabled>No published pages</SelectItem>}
                      {pages.map(p => <SelectItem key={p.slug} value={p.slug}>{(p.title?.en || p.title?.fr || p.slug)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label className="text-xs font-semibold">External URL</Label>
                  <Input placeholder="https://example.com" value={dialog.data.url} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, url: e.target.value } })} data-testid="nav-url-input" />
                </div>
              )}
              <div className="flex items-center justify-between border border-border rounded-md px-3 py-2">
                <div>
                  <Label className="text-xs font-semibold">Active</Label>
                  <p className="text-[11px] text-text_muted">Turn off to hide this link.</p>
                </div>
                <Switch checked={dialog.data.is_active !== false} onCheckedChange={(v) => setDialog({ ...dialog, data: { ...dialog.data, is_active: v } })} data-testid="nav-active-switch" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, mode: 'create', data: null })}>Cancel</Button>
            <Button onClick={save} data-testid="nav-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete nav link?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes <strong>{confirmDelete?.title_i18n?.en || confirmDelete?.title_i18n?.fr || confirmDelete?.url}</strong>.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-danger hover:bg-danger" onClick={async () => { await remove(confirmDelete); setConfirmDelete(null); }} data-testid="nav-confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
